/**
 * RevisionStatusBadge Component
 *
 * Color-coded status badge with icon for revision statuses.
 */

'use client';

import {cva, type VariantProps} from 'class-variance-authority';
import {CircleDot, Clock, CheckCircle2, Archive, Ban} from 'lucide-react';
import {RevisionStatus, STATUS_LABELS} from '../types';

const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-full font-medium',
  {
    variants: {
      status: {
        [RevisionStatus.DRAFT]: 'bg-yellow-100 text-yellow-700',
        [RevisionStatus.IN_REVIEW]: 'bg-blue-100 text-blue-700',
        [RevisionStatus.APPROVED]: 'bg-blue-100 text-blue-700',
        [RevisionStatus.PUBLISHED]: 'bg-green-100 text-green-700',
        [RevisionStatus.ARCHIVED]: 'bg-gray-100 text-gray-700',
        [RevisionStatus.RETIRED]: 'bg-red-100 text-red-700',
      },
      size: {
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-2.5 py-1 text-sm',
        lg: 'px-3 py-1.5 text-base',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

const iconSizes = {
  sm: 'h-3 w-3',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
} as const;

const statusIcons = {
  [RevisionStatus.DRAFT]: CircleDot,
  [RevisionStatus.IN_REVIEW]: Clock,
  [RevisionStatus.APPROVED]: CheckCircle2,
  [RevisionStatus.PUBLISHED]: CheckCircle2,
  [RevisionStatus.ARCHIVED]: Archive,
  [RevisionStatus.RETIRED]: Ban,
} as const;

type RevisionStatusBadgeProps = VariantProps<typeof badgeVariants> & {
  status: RevisionStatus;
};

export function RevisionStatusBadge({
  status,
  size = 'md',
}: RevisionStatusBadgeProps) {
  const IconComponent = statusIcons[status];
  const iconSize = iconSizes[size!];

  return (
    <span className={badgeVariants({status, size})}>
      <IconComponent className={iconSize} />
      {STATUS_LABELS[status]}
    </span>
  );
}
